import { ChevronLeft, ChevronRight } from "lucide-react"
import { Button } from '@/components/ui/button'
import {
  DEBIT_CARD_DESIGN_IDS,
  type DebitCardDesignId,
  DEFAULT_DEBIT_CARD_DESIGN_ID,
  buildDebitCardDesignMap,
  debitCardDesignIdOrDefault,
  getDebitCardDesignLabel,
  isDebitCardDesignId,
} from '@dao/lib/debit-card-design'

export {
  DEBIT_CARD_DESIGN_IDS,
  type DebitCardDesignId,
  DEFAULT_DEBIT_CARD_DESIGN_ID,
  buildDebitCardDesignMap,
  debitCardDesignIdOrDefault,
  getDebitCardDesignLabel,
  isDebitCardDesignId,
}

export const DEBIT_CARD_DESIGNS = [
  {
    id: 'clownfish',
    label: 'Orange Fish',
    imageUrl: new URL('../../assets/debit-cards/MC_ClimateFirstBank_12[51].png', import.meta.url).href,
  },
  {
    id: 'frog',
    label: 'Frog',
    imageUrl: new URL('../../assets/debit-cards/MC_ClimateFirstBank_7[15].png', import.meta.url).href,
  },
  {
    id: 'macaw',
    label: 'Parrot',
    imageUrl: new URL('../../assets/debit-cards/MC_ClimateFirstBank_8[43].png', import.meta.url).href,
  },
  {
    id: 'monarch',
    label: 'Butterfly',
    imageUrl: new URL('../../assets/debit-cards/MC_ClimateFirstBank_10[27].png', import.meta.url).href,
  },
  {
    id: 'triggerfish',
    label: 'Blue Fish',
    imageUrl: new URL('../../assets/debit-cards/MC_ClimateFirstBank_9[95].png', import.meta.url).href,
  },
  {
    id: 'jellyfish',
    label: 'Caterpillar',
    imageUrl: new URL('../../assets/debit-cards/MC_ClimateFirstBank_11[80].png', import.meta.url).href,
  },
  {
    id: 'blue',
    label: 'Blue',
    imageUrl: new URL('../../assets/debit-cards/MC_ClimateFirstBank_Blue[15].png', import.meta.url).href,
  },
  {
    id: 'black',
    label: 'Black',
    imageUrl: new URL('../../assets/debit-cards/MC_ClimateFirstBank_Black[54].png', import.meta.url).href,
  },
] as const satisfies ReadonlyArray<{ id: DebitCardDesignId; label: string; imageUrl: string }>

/** Cap preview height so the carousel does not grow with viewport width. */
const DEBIT_CARD_PREVIEW_MAX_HEIGHT_PX = 300
const DEBIT_CARD_PREVIEW_ASPECT_RATIO = 8 / 5
const DEBIT_CARD_PREVIEW_MAX_WIDTH_PX = DEBIT_CARD_PREVIEW_MAX_HEIGHT_PX * DEBIT_CARD_PREVIEW_ASPECT_RATIO
/** Left/right arrows (36px each), gaps, label padding, and border. */
const DEBIT_CARD_CAROUSEL_CHROME_PX = 114
const DEBIT_CARD_CAROUSEL_MAX_WIDTH_PX = DEBIT_CARD_PREVIEW_MAX_WIDTH_PX + DEBIT_CARD_CAROUSEL_CHROME_PX

interface DebitCardDesignPickerProps {
  label: string
  name: string
  value: DebitCardDesignId
  onChange: (value: DebitCardDesignId) => void
}

export function DebitCardDesignPicker({ label, name, value, onChange }: DebitCardDesignPickerProps) {
  const selectedIndex = DEBIT_CARD_DESIGNS.findIndex((design) => design.id === value)
  const activeIndex = selectedIndex >= 0 ? selectedIndex : 0
  const activeDesign = DEBIT_CARD_DESIGNS[activeIndex]

  const selectByOffset = (offset: number) => {
    const nextIndex = (activeIndex + offset + DEBIT_CARD_DESIGNS.length) % DEBIT_CARD_DESIGNS.length
    onChange(DEBIT_CARD_DESIGNS[nextIndex].id)
  }

  return (
    <fieldset className="space-y-3 border border-cfb-shadow bg-cfb-light p-3">
      <legend className="sr-only">{label}</legend>
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-sm font-semibold text-gray-900">Select your card design</div>
          <div className="text-xs text-gray-500">{label}</div>
        </div>
        <div className="text-xs font-medium text-gray-500">
          {activeIndex + 1} of {DEBIT_CARD_DESIGNS.length}
        </div>
      </div>
      <div
        className="mr-auto flex w-full items-center gap-3"
        style={{ maxWidth: DEBIT_CARD_CAROUSEL_MAX_WIDTH_PX }}
      >
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0"
          aria-label={`Show previous ${label.toLowerCase()}`}
          onClick={() => selectByOffset(-1)}
        >
          <ChevronLeft className="h-4 w-4" />
        </Button>
        <label
          className="min-w-0 flex-1 cursor-pointer rounded-lg border border-brand bg-brand-soft p-2 ring-2 ring-brand/30 transition-all"
        >
          <input
            type="radio"
            className="sr-only"
            name={name}
            value={activeDesign.id}
            checked
            onChange={() => onChange(activeDesign.id)}
          />
          <div className="relative overflow-hidden rounded-md">
            <img
              src={activeDesign.imageUrl}
              alt=""
              className="aspect-[8/5] w-full object-cover"
              style={{ maxHeight: DEBIT_CARD_PREVIEW_MAX_HEIGHT_PX }}
            />
            <span className="absolute bottom-2 left-2 rounded bg-cfb-light/90 px-2 py-1 text-xs font-semibold text-gray-800 shadow-sm">
              {activeDesign.label}
            </span>
          </div>
        </label>
        <Button
          type="button"
          variant="outline"
          size="icon"
          className="h-9 w-9 shrink-0"
          aria-label={`Show next ${label.toLowerCase()}`}
          onClick={() => selectByOffset(1)}
        >
          <ChevronRight className="h-4 w-4" />
        </Button>
      </div>
    </fieldset>
  )
}
